import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TikTokFormSummary {
  form_id: string;
  form_name: string;
  page_id?: string;
  status?: string;
}

export interface TikTokFormField {
  key: string;
  label: string;
  type: string;
}

export interface TikTokMappingRule {
  target: string;
  label?: string;
  crm_use?: boolean;
  analytics_use?: boolean;
  ai_profile_use?: boolean;
  sensitive?: boolean;
  workflow_key?: string;
}

export interface TikTokConnectionState {
  connections: Array<Record<string, any>>;
  forms: Array<Record<string, any>>;
  failures: Array<Record<string, any>>;
}

async function invokeTikTok(
  businessId: string,
  action: string,
  input: Record<string, unknown> = {},
) {
  const { data, error } = await supabase.functions.invoke("tiktok-connector", {
    body: { action, business_id: businessId, ...input },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function fetchTikTokStatus(
  businessId: string,
): Promise<TikTokConnectionState> {
  try {
    return (await invokeTikTok(businessId, "status")) as TikTokConnectionState;
  } catch {
    try {
      const db = supabase as any;
      const [{ data: connections }, { data: forms }] = await Promise.all([
        db
          .from("tiktok_connections")
          .select(
            "id, business_id, display_name, auth_mode, status, advertiser_id, business_account_id, open_id, scopes, token_expires_at, refresh_token_expires_at, account_metadata, messaging_enabled, lead_gen_enabled, last_health_check_at, last_error, connected_at, disconnected_at, created_at, updated_at",
          )
          .eq("business_id", businessId)
          .order("created_at", { ascending: false }),
        db
          .from("tiktok_form_connections")
          .select(
            "id, business_id, tiktok_connection_id, tiktok_form_id, tiktok_page_id, advertiser_id, form_name, form_status, form_schema, field_mapping, mapping_version, webhook_id, is_enabled, live_automation_enabled, backfill_automation_enabled, raw_retention_days, reconcile_state, last_reconciled_at, last_lead_at, last_error, created_at, updated_at",
          )
          .eq("business_id", businessId)
          .order("created_at", { ascending: false }),
      ]);
      return {
        connections: connections ?? [],
        forms: forms ?? [],
        failures: [],
      };
    } catch {
      return {
        connections: [],
        forms: [],
        failures: [],
      };
    }
  }
}

export function useTikTokConnection(businessId?: string | null) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ["tiktok-connection", businessId] as const,
    [businessId],
  );

  const status = useQuery({
    queryKey,
    queryFn: () => fetchTikTokStatus(businessId!),
    enabled: !!businessId,
    retry: false,
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  const mutation = useMutation({
    mutationFn: async ({
      action,
      input,
    }: {
      action: string;
      input?: Record<string, unknown>;
    }) => invokeTikTok(businessId!, action, input),
    onSuccess: refresh,
  });

  return {
    ...status,
    state: status.data,
    busy: mutation.isPending,
    mutateAsync: mutation.mutateAsync,
    refresh,
  };
}
